import { create } from 'zustand';
import type { ModerationStats } from '../../sdk/@types/Forum';
import type { ApiError } from '../../sdk/errors/apiError';

/**
 * `GET /moderation/stats`, shared across every mounted `useNotifications()`
 * instance (the bell, the Sidebar entry, `/admin/moderacao` itself).
 *
 * `useNotifications` used to keep this in local `useState`, so each instance
 * had its own copy: a moderator approving a comment on `/admin/moderacao`
 * updated only that page's copy, and the header bell / Sidebar badges (each
 * their own hook call, no shared state) stayed at the stale count until the
 * next navigation re-triggered their own fetch. Lifting the counters here — a
 * zustand singleton, same pattern as `useProfileStore` — makes
 * `applyDelta` an instant, cross-instance decrement: whichever screen decides
 * a comment or a report calls it once, and every reader of the store
 * re-renders with the same object, no navigation required.
 */
interface ModerationCountersState {
  stats: ModerationStats | null;
  /** Set only when the last fetch failed as a recognized permission denial. */
  denial: ApiError | null;
  setStats: (stats: ModerationStats | null) => void;
  setDenial: (denial: ApiError | null) => void;
  /**
   * Subtracts from the counters that already left the queue — never counts
   * up. Positive numbers only; a no-op while `stats` hasn't loaded yet.
   */
  applyDelta: (delta: { pendingComments?: number; openReports?: number }) => void;
}

export const useModerationCountersStore = create<ModerationCountersState>((set) => ({
  stats: null,
  denial: null,
  setStats: (stats) => set({ stats }),
  setDenial: (denial) => set({ denial }),
  applyDelta: (delta) =>
    set((state) => {
      if (!state.stats) return state;
      return {
        stats: {
          ...state.stats,
          pendingComments: Math.max(
            0,
            state.stats.pendingComments - (delta.pendingComments ?? 0)
          ),
          openReports: Math.max(0, state.stats.openReports - (delta.openReports ?? 0)),
        },
      };
    }),
}));
